import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faCloudArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { commonMessages } from 'src/features/auth/utils';
import { useCreateDSKPaymentProvider } from 'src/features/parkingManagement/api/paymentPaymentProviders/useCreateDSKPaymentProvider';
import { useCreateMyPosPaymentProvider } from 'src/features/parkingManagement/api/paymentPaymentProviders/useCreateMyPOSPaymentProvider';
import {
    type ParkingEntity,
    useCreateParkingEntity,
} from 'src/features/parkingManagement/api/paymentPaymentProviders/useCreateParkingEntity';
import { useDeleteDSKPaymentProvider } from 'src/features/parkingManagement/api/paymentPaymentProviders/useDeleteDSKPaymentProvider';
import { useDeleteMyPOSPaymentProvider } from 'src/features/parkingManagement/api/paymentPaymentProviders/useDeleteMyPOSPaymentProvider';
import { useDeleteParkingEntity } from 'src/features/parkingManagement/api/paymentPaymentProviders/useDeleteParkingEntity';
import {
    DSK_PAYMENT_PROVIDER_RESOURCE,
    type DSKPaymentProvider,
    useGetDSKPaymentProvider,
} from 'src/features/parkingManagement/api/paymentPaymentProviders/useGetDSKPaymentProvider';
import {
    MyPOS_PAYMENT_PROVIDER_RESOURCE,
    useGetMyPOSPaymentProvider,
} from 'src/features/parkingManagement/api/paymentPaymentProviders/useGetMyPOSPaymentProvider';
import {
    PARKING_ENTITY_RESOURCE,
    useGetParkingEntity,
} from 'src/features/parkingManagement/api/paymentPaymentProviders/useGetParkingEntity';
import { useUpdateDSKPaymentProvider } from 'src/features/parkingManagement/api/paymentPaymentProviders/useUpdateDSKPaymentProvider';
import {
    type UpdateMyPosPaymentProviderType,
    useUpdateMyPosPaymentProvider,
} from 'src/features/parkingManagement/api/paymentPaymentProviders/useUpdateMyPosPaymentProvider';
import { useUpdateParkingEntity } from 'src/features/parkingManagement/api/paymentPaymentProviders/useUpdateParkingEntity';
import { type UpdateParkingDTO, useUpdateParking } from 'src/features/parkingManagement/api/updateParking';
import {
    BORICA_PAYMENTS_RESOURCE,
    useBoricaPaymentsStatus,
} from 'src/features/parkingManagement/api/useBoricaPaymentsStatus';
import { useDisableBoricaPayments } from 'src/features/parkingManagement/api/useDisableBoricaPayments';
import { useEnableBoricaPayments } from 'src/features/parkingManagement/api/useEnableBoricaPayments';
import { GET_PARKING_RESOURCE, useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import {
    formSchema,
    PAYMENT_PROVIDERS,
    type PaymentProviderFormSchemaType,
} from 'src/features/parkingManagement/utils/paymentProviders';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { GeneralBlock, GeneralBlockBody } from 'src/registry/components/blocks/GeneralBlock';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { useTitleStore } from 'src/stores/useTitleStore';
import { mapErrors } from 'src/utils/errors';
import { generalErrorMessages, generalLabelMessages, generalMessages } from 'src/utils/messages';

type OwnPaymentProviderFieldName = Extract<
    keyof PaymentProviderFormSchemaType,
    | 'store_id'
    | 'client_number'
    | 'key_index'
    | 'username'
    | 'password'
    | 'subdomain'
    | 'name'
    | 'address'
    | 'uic'
    | 'tax_number'
    | 'email'
    | 'phone'
>;

type FileFieldProps<T extends keyof PaymentProviderFormSchemaType> = {
    fieldName: T;
    fieldLabel: { id: string; defaultMessage: string };
};

const PaymentProviders = () => {
    const { formatMessage } = useIntl();

    const { parkingId } = useParams();
    const { toast } = useToast();
    const { setTitle, removeTitle } = useTitleStore();
    const queryClient = useQueryClient();

    const [uploadedFiles, setUploadedFiles] = React.useState<Record<string, string>>({});
    const [previousPaymentProvider, setPreviousPaymentProvider] = React.useState<PAYMENT_PROVIDERS | undefined>(
        undefined,
    );
    const [previousHasOwnProvider, setPreviousHasOwnProvider] = React.useState<boolean>(false);

    const enabledFormFields = {
        [formatMessage(generalMessages.yes)]: 'true',
        [formatMessage(generalMessages.no)]: 'false',
    };

    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });
    const { data: boricaPaymentsStatus } = useBoricaPaymentsStatus({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const { data: dskPaymentProvider } = useGetDSKPaymentProvider({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });
    const { data: myPosPaymentProvider } = useGetMyPOSPaymentProvider({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });

    const { data: parkingEntity } = useGetParkingEntity({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });

    const defaultValues = {
        are_borica_payments_enabled: boricaPaymentsStatus?.are_borica_payments_enabled,
        serial_id: (data?.serial_id && String(data.serial_id)) || '',
        has_own_payment_provider: data?.has_own_payment_provider ?? false,
        own_payment_provider:
            myPosPaymentProvider && myPosPaymentProvider?.count > 0
                ? PAYMENT_PROVIDERS.MyPOS
                : dskPaymentProvider && dskPaymentProvider?.count > 0
                  ? PAYMENT_PROVIDERS.DSK
                  : undefined,
        store_id: myPosPaymentProvider && myPosPaymentProvider?.results[0]?.store_id,
        client_number: myPosPaymentProvider && myPosPaymentProvider?.results[0]?.client_number,
        key_index: myPosPaymentProvider && myPosPaymentProvider?.results[0]?.key_index,
        username: dskPaymentProvider?.results[0]?.username ?? '',
        password: dskPaymentProvider?.results[0]?.password ?? '',
        public_certificate: undefined,
        private_key: undefined,
        name: parkingEntity?.results[0]?.name,
        address: parkingEntity?.results[0]?.address,
        uic: parkingEntity?.results[0]?.uic,
        tax_number: parkingEntity?.results[0]?.tax_number,
        email: parkingEntity?.results[0]?.email ?? null,
        phone: parkingEntity?.results[0]?.phone ?? null,
        subdomain: data?.subdomain || '',
        _isEditMode: myPosPaymentProvider && myPosPaymentProvider.count > 0,
    };

    const form = useForm<PaymentProviderFormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues,
    });

    const showOwnPaymentProvidersOptions = form.watch('has_own_payment_provider') === true;

    const { mutateAsync, isPending } = useUpdateParking({
        onSuccess: () => {
            toast({
                title: formatMessage(parkingManagementMessages.parkingChangeSuccess),
                description: formatMessage(parkingManagementMessages.parkingChangeSuccessDetails, { name: data?.name }),
            });
        },
        onError: (e) => {
            if (e?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.detail}`,
                });
            } else if (e?.response?.data) {
                mapErrors(e.response.data, form.setError);
            }
        },
    });

    const { mutate: enableBorica } = useEnableBoricaPayments({
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [BORICA_PAYMENTS_RESOURCE, { parkingId }] }),
        onError: (e) => {
            const boricaSettingExists = 'parking_borica_setting_already_exists';
            const errorCode = e.response?.data?.code;
            const description =
                errorCode == boricaSettingExists
                    ? formatMessage(parkingManagementMessages.boricaAlreadyExists)
                    : e.response?.data?.message;
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description,
            });
        },
    });
    const { mutate: disableBorica } = useDisableBoricaPayments({
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [BORICA_PAYMENTS_RESOURCE, { parkingId }] }),
        onError: (e) => {
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: e.response?.data?.message,
            });
        },
    });

    const { mutateAsync: deleteMyPosPaymentProvider } = useDeleteMyPOSPaymentProvider({
        onError: () => {
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: `${formatMessage(parkingManagementMessages.paymentProviderDeleteErrorDetails)}`,
            });
        },
    });
    const { mutateAsync: deleteDSKPaymentProvider } = useDeleteDSKPaymentProvider({
        onError: () => {
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: `${formatMessage(parkingManagementMessages.paymentProviderDeleteErrorDetails)}`,
            });
        },
    });

    const { mutateAsync: deleteParkingEntity } = useDeleteParkingEntity({});

    const { mutateAsync: createDSKPaymentProvider } = useCreateDSKPaymentProvider({
        onSuccess: () => {
            toast({
                title: formatMessage(parkingManagementMessages.paymentProviderChangeSuccess),
                description: formatMessage(parkingManagementMessages.paymentProviderChangeSuccessDetails, {
                    name: data?.name,
                }),
            });
        },
        onError: () => {
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: `${formatMessage(generalErrorMessages.somethingWentWrongRetry)}`,
            });
        },
    });
    const { mutateAsync: createMyPosPaymentProvider } = useCreateMyPosPaymentProvider({
        onSuccess: () => {
            toast({
                title: formatMessage(parkingManagementMessages.paymentProviderChangeSuccess),
                description: formatMessage(parkingManagementMessages.paymentProviderChangeSuccessDetails, {
                    name: data?.name,
                }),
            });
        },
        onError: (e) => {
            const error = Object.entries(e?.response?.data || {})[0];
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: error
                    ? `${error[0]}, ${error[1][0]}`
                    : formatMessage(generalErrorMessages.somethingWentWrongRetry),
            });
        },
    });

    const { mutateAsync: createParkingEntity } = useCreateParkingEntity({});

    const { mutate: updateParkingEntity } = useUpdateParkingEntity({
        onSuccess: (updates) => {
            form.reset({
                ...form.getValues(),
                ...updates,
            });
        },
        onError: () => {
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: `${formatMessage(generalErrorMessages.somethingWentWrongRetry)}`,
            });
        },
    });

    const { mutate: updateDSKPaymentProvider } = useUpdateDSKPaymentProvider({
        onSuccess: (updates) => {
            form.reset({
                ...form.getValues(),
                ...updates,
            });
        },
        onError: () => {
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: `${formatMessage(generalErrorMessages.somethingWentWrongRetry)}`,
            });
        },
    });
    const { mutate: updateMyPosPaymentProvider } = useUpdateMyPosPaymentProvider({
        onSuccess: (updates) => {
            form.reset({
                ...form.getValues(),
                ...Object.fromEntries(
                    Object.entries(updates).filter(([key]) => key !== 'public_certificate' && key !== 'private_key'),
                ),
            });
        },
        onError: (e) => {
            const error = Object.entries(e?.response?.data || {})[0];
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: error
                    ? `${error[0]}, ${error[1][0]}`
                    : formatMessage(generalErrorMessages.somethingWentWrongRetry),
            });
        },
    });

    React.useEffect(() => {
        if (!data || !myPosPaymentProvider || !dskPaymentProvider) return;
        setTitle(data.name);
        setPreviousHasOwnProvider(data.has_own_payment_provider);
        form.reset(defaultValues);

        if (dskPaymentProvider && myPosPaymentProvider) {
            setPreviousPaymentProvider(
                myPosPaymentProvider?.count > 0
                    ? PAYMENT_PROVIDERS.MyPOS
                    : dskPaymentProvider?.count > 0
                      ? PAYMENT_PROVIDERS.DSK
                      : undefined,
            );
        }
        return () => {
            removeTitle();
            form.reset();
        };
    }, [
        data,
        formatMessage,
        boricaPaymentsStatus,
        dskPaymentProvider?.count,
        myPosPaymentProvider?.count,
        parkingEntity,
        myPosPaymentProvider,
        dskPaymentProvider,
        setTitle,
        form,
        removeTitle,
    ]);

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        fieldName: keyof PaymentProviderFormSchemaType,
    ) => {
        const file = e.target.files?.[0];
        if (!file) return;

        form.setValue(fieldName, file, {
            shouldValidate: true,
            shouldDirty: true,
        });

        setUploadedFiles((prev) => ({
            ...prev,
            [fieldName]: file.name,
        }));
    };

    const onSubmit: SubmitHandler<PaymentProviderFormSchemaType> = React.useCallback(
        async (values: PaymentProviderFormSchemaType) => {
            try {
                const {
                    serial_id,
                    has_own_payment_provider,
                    username,
                    password,
                    store_id,
                    client_number,
                    key_index,
                    public_certificate,
                    private_key,
                    subdomain,
                    name,
                    address,
                    email,
                    phone,
                    uic,
                    tax_number,
                } = values;

                const currentProvider: PAYMENT_PROVIDERS | undefined = values.own_payment_provider;

                if (form.formState.dirtyFields.are_borica_payments_enabled) {
                    if (!values.are_borica_payments_enabled && data?.serial_id) {
                        disableBorica({ parkingId: parkingId || '' });
                    }
                    if (values.are_borica_payments_enabled) {
                        enableBorica({ parkingId: parkingId || '' });
                    }
                }

                if (previousHasOwnProvider && values.has_own_payment_provider === false) {
                    switch (previousPaymentProvider) {
                        case PAYMENT_PROVIDERS.MyPOS:
                            if (myPosPaymentProvider && parkingEntity) {
                                if (!parkingId) return;
                                await deleteMyPosPaymentProvider({
                                    parkingId,
                                    id: myPosPaymentProvider?.results[0]?.id,
                                });
                                await deleteParkingEntity({ parkingId, id: parkingEntity?.results[0]?.id });
                                await mutateAsync({ parkingId, subdomain: null });
                            }
                            break;
                        case PAYMENT_PROVIDERS.DSK:
                            if (dskPaymentProvider && parkingEntity) {
                                if (!parkingId) return;
                                await deleteDSKPaymentProvider({ parkingId, id: dskPaymentProvider?.results[0]?.id });
                                await deleteParkingEntity({ parkingId, id: parkingEntity?.results[0]?.id });
                            }
                            break;
                    }
                    await mutateAsync({ parkingId, serial_id, has_own_payment_provider, subdomain: null });
                }

                if (previousPaymentProvider && previousPaymentProvider !== currentProvider) {
                    switch (previousPaymentProvider) {
                        case PAYMENT_PROVIDERS.MyPOS:
                            if (myPosPaymentProvider) {
                                if (!parkingId) return;
                                await deleteMyPosPaymentProvider({
                                    parkingId,
                                    id: myPosPaymentProvider?.results[0]?.id,
                                });
                                await mutateAsync({ parkingId, subdomain: null });
                            }
                            break;
                        case PAYMENT_PROVIDERS.DSK:
                            if (dskPaymentProvider) {
                                if (!parkingId) return;
                                await deleteDSKPaymentProvider({ parkingId, id: dskPaymentProvider?.results[0]?.id });
                            }
                            break;
                    }
                }

                if (values.has_own_payment_provider) {
                    const dirtyFields = form.formState.dirtyFields;
                    const payload: Partial<ParkingEntity> = {};

                    if (parkingEntity && parkingEntity?.count > 0) {
                        if (!parkingId) return;
                        if (dirtyFields.name) {
                            payload.name = values.name;
                        }
                        if (dirtyFields.address) {
                            payload.address = values.address;
                        }
                        if (dirtyFields.uic) {
                            payload.uic = values.uic;
                        }
                        if (dirtyFields.tax_number) {
                            payload.tax_number = values.tax_number;
                        }
                        if (dirtyFields.email) {
                            payload.email = values.email;
                        }
                        if (dirtyFields.phone) {
                            payload.phone = values.phone;
                        }

                        if (Object.keys(payload).length > 0) {
                            updateParkingEntity({
                                parkingId,
                                id: parkingEntity.results[0]?.id,
                                ...payload,
                            });
                        }
                    }

                    if (parkingEntity && parkingEntity?.count === 0) {
                        if (!name || !address || !uic || !tax_number || !parkingId) return null;
                        await createParkingEntity({ parkingId, name, address, uic, tax_number, email, phone });
                    }
                }

                if (values.has_own_payment_provider && values.own_payment_provider === PAYMENT_PROVIDERS.DSK) {
                    const dirtyFields = form.formState.dirtyFields;
                    const payload: Partial<DSKPaymentProvider> = {};

                    if (dskPaymentProvider && dskPaymentProvider?.count > 0) {
                        if (dirtyFields.username) {
                            payload.username = values.username;
                        }
                        if (dirtyFields.password) {
                            payload.password = values.password;
                        }
                        if (Object.keys(payload).length > 0) {
                            if (!parkingId) return;
                            updateDSKPaymentProvider({
                                parkingId,
                                id: dskPaymentProvider.results[0]?.id,
                                ...payload,
                            });
                        }
                    }

                    if (dskPaymentProvider && dskPaymentProvider?.count === 0) {
                        if (!username || !password || !parkingId) return null;

                        await createDSKPaymentProvider({
                            parkingId,
                            username,
                            password,
                        });
                    }
                }

                if (values.has_own_payment_provider && values.own_payment_provider === PAYMENT_PROVIDERS.MyPOS) {
                    const dirtyFields = form.formState.dirtyFields;
                    const payload: Partial<UpdateMyPosPaymentProviderType> = {};
                    const parkingPayload: Partial<UpdateParkingDTO> = {};

                    if (myPosPaymentProvider && myPosPaymentProvider?.count > 0) {
                        if (dirtyFields.store_id) {
                            payload.store_id = values.store_id;
                        }
                        if (dirtyFields.client_number) {
                            payload.client_number = values.client_number;
                        }

                        if (dirtyFields.key_index) {
                            payload.key_index = values.key_index;
                        }
                        if (dirtyFields.private_key) {
                            payload.private_key = values.private_key;
                        }
                        if (dirtyFields.public_certificate) {
                            payload.public_certificate = values.public_certificate;
                        }

                        if (dirtyFields.subdomain) {
                            parkingPayload.subdomain = values.subdomain;
                        }

                        if (Object.keys(payload).length > 0) {
                            if (!parkingId) return;
                            updateMyPosPaymentProvider({
                                parkingId,
                                id: myPosPaymentProvider.results[0]?.id,
                                ...payload,
                            });
                        }

                        if (Object.keys(parkingPayload).length > 0) {
                            await mutateAsync({ parkingId, ...parkingPayload });
                        }
                    }

                    if (myPosPaymentProvider && myPosPaymentProvider?.count === 0) {
                        if (!public_certificate || !private_key || !parkingId) return null;
                        await createMyPosPaymentProvider({
                            parkingId,
                            store_id: store_id || '',
                            client_number: client_number || '',
                            key_index,
                            public_certificate,
                            private_key,
                        });
                        await mutateAsync({ parkingId, subdomain });
                    }
                }

                setPreviousPaymentProvider(currentProvider);
                setUploadedFiles({});

                void Promise.all([
                    queryClient.invalidateQueries({ queryKey: [MyPOS_PAYMENT_PROVIDER_RESOURCE, { parkingId }] }),
                    queryClient.invalidateQueries({ queryKey: [DSK_PAYMENT_PROVIDER_RESOURCE, { parkingId }] }),
                    queryClient.invalidateQueries({ queryKey: [GET_PARKING_RESOURCE, { parkingId }] }),
                    queryClient.invalidateQueries({ queryKey: [PARKING_ENTITY_RESOURCE, { parkingId }] }),
                ]);
                form.reset();
            } catch (error) {
                console.error(error);
            }
        },
        [
            createDSKPaymentProvider,
            createMyPosPaymentProvider,
            createParkingEntity,
            data?.serial_id,
            deleteDSKPaymentProvider,
            deleteMyPosPaymentProvider,
            deleteParkingEntity,
            disableBorica,
            dskPaymentProvider,
            enableBorica,
            form.formState.dirtyFields,
            mutateAsync,
            myPosPaymentProvider,
            parkingEntity,
            parkingId,
            previousHasOwnProvider,
            previousPaymentProvider,
            queryClient,
            updateDSKPaymentProvider,
            updateMyPosPaymentProvider,
            updateParkingEntity,
        ],
    );

    const getOwnPaymentProviderFormFieldBlock = ({
        fieldName,
        fieldLabel,
    }: {
        fieldName: OwnPaymentProviderFieldName;
        fieldLabel: { id: string; defaultMessage: string };
    }) => {
        return (
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(fieldLabel)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <FormField
                        control={form.control}
                        name={fieldName}
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Input
                                        type="text"
                                        {...field}
                                        placeholder={formatMessage(fieldLabel)}
                                        value={field.value ?? ''}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
        );
    };

    const getFileUploadFormFieldBlock = <T extends keyof PaymentProviderFormSchemaType>({
        fieldName,
        fieldLabel,
    }: FileFieldProps<T>) => {
        return (
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(fieldLabel)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <FormField
                        control={form.control}
                        name={fieldName}
                        render={() => (
                            <FormItem>
                                <FormControl>
                                    <label htmlFor={fieldName} className="cursor-pointer">
                                        <GeneralBlock>
                                            <GeneralBlockBody className="text-primary-700 flex items-center justify-center font-bold">
                                                <div className="flex flex-col items-center">
                                                    <div className="flex flex-row justify-center gap-2">
                                                        <FontAwesomeIcon
                                                            icon={faCloudArrowUp}
                                                            size="lg"
                                                            className="text-primary-700"
                                                        />
                                                        <span>
                                                            {formatMessage(parkingManagementMessages.clickToUpload)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        {uploadedFiles[fieldName] && (
                                                            <span className="text-sm font-semibold text-gray-500">
                                                                {uploadedFiles[fieldName]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <input
                                                    type="file"
                                                    accept=".pem"
                                                    id={fieldName}
                                                    className="hidden"
                                                    onChange={(e) => handleFileChange(e, fieldName)}
                                                />
                                            </GeneralBlockBody>
                                        </GeneralBlock>
                                    </label>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
        );
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlockTitle className="font-bold">Borica</FormFieldBlockTitle>
            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.enabled)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="are_borica_payments_enabled"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Select
                                        onValueChange={(val) => field.onChange(val === 'true')}
                                        value={String(field.value)}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={formatMessage(parkingManagementMessages.enabled)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(enabledFormFields).map(([key, value]) => {
                                                return (
                                                    <SelectItem key={key} value={value}>
                                                        {key}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>Borica ID</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="serial_id"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input type="text" readOnly disabled {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlockTitle className="font-bold">
                <FormattedMessage {...parkingManagementMessages.ownPaymentOperator} />
            </FormFieldBlockTitle>
            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>
                            {formatMessage(parkingManagementMessages.ownPaymentOperator)}
                        </FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <FormField
                        control={form.control}
                        name="has_own_payment_provider"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Select
                                        onValueChange={(val) => field.onChange(val === 'true')}
                                        value={String(field.value ?? false)}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={formatMessage(
                                                    parkingManagementMessages.ownPaymentOperator,
                                                )}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(enabledFormFields).map(([key, value]) => {
                                                return (
                                                    <SelectItem key={key} value={value}>
                                                        {key}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            {showOwnPaymentProvidersOptions && (
                <>
                    <FormFieldBlock>
                        <FormFieldBlockLabel>
                            <FormFieldBlockTitle>
                                <FormFieldBlockName>
                                    {formatMessage(parkingManagementMessages.paymentOperator)}
                                </FormFieldBlockName>
                            </FormFieldBlockTitle>
                        </FormFieldBlockLabel>
                        <FormFieldBlockBody className="w-full">
                            <FormField
                                control={form.control}
                                name="own_payment_provider"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue
                                                        placeholder={formatMessage(
                                                            parkingManagementMessages.paymentOperator,
                                                        )}
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.values(PAYMENT_PROVIDERS).map((provider) => {
                                                        return (
                                                            <SelectItem key={provider} value={provider}>
                                                                {provider}
                                                            </SelectItem>
                                                        );
                                                    })}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </FormFieldBlockBody>
                    </FormFieldBlock>

                    {form.watch('own_payment_provider') === PAYMENT_PROVIDERS.DSK && (
                        <>
                            {getOwnPaymentProviderFormFieldBlock({
                                fieldName: 'username',
                                fieldLabel: parkingManagementMessages.username,
                            })}
                            {getOwnPaymentProviderFormFieldBlock({
                                fieldName: 'password',
                                fieldLabel: commonMessages.password,
                            })}
                        </>
                    )}

                    {form.watch('own_payment_provider') === PAYMENT_PROVIDERS.MyPOS && (
                        <>
                            {getFileUploadFormFieldBlock({
                                fieldName: 'public_certificate',
                                fieldLabel: parkingManagementMessages.myPosCertificate,
                            })}
                            {getFileUploadFormFieldBlock({
                                fieldName: 'private_key',
                                fieldLabel: parkingManagementMessages.myPosPrivateKey,
                            })}
                            {getOwnPaymentProviderFormFieldBlock({
                                fieldName: 'store_id',
                                fieldLabel: parkingManagementMessages.myPosStoreId,
                            })}
                            {getOwnPaymentProviderFormFieldBlock({
                                fieldName: 'client_number',
                                fieldLabel: parkingManagementMessages.myPosClientNumber,
                            })}
                            <FormFieldBlock>
                                <FormFieldBlockLabel>
                                    <FormFieldBlockTitle>
                                        <FormFieldBlockName>
                                            {formatMessage(parkingManagementMessages.myPosKeyIndex)}
                                        </FormFieldBlockName>
                                    </FormFieldBlockTitle>
                                </FormFieldBlockLabel>
                                <FormFieldBlockBody className="w-full">
                                    <FormField
                                        control={form.control}
                                        name="key_index"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        pattern="[0-9]*"
                                                        {...field}
                                                        placeholder={formatMessage(
                                                            parkingManagementMessages.myPosKeyIndex,
                                                        )}
                                                        value={field.value ?? ''}
                                                        onKeyDown={(e) => {
                                                            if (e.key === '-' || e.key === '+' || e.key === 'e') {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </FormFieldBlockBody>
                            </FormFieldBlock>

                            {getOwnPaymentProviderFormFieldBlock({
                                fieldName: 'subdomain',
                                fieldLabel: parkingManagementMessages.subdomain,
                            })}
                        </>
                    )}

                    <FormFieldBlockSeparator />

                    <FormFieldBlockTitle className="font-bold">
                        <FormattedMessage {...parkingManagementMessages.digitalReceiptData} />
                    </FormFieldBlockTitle>
                    <FormFieldBlockSeparator />

                    {getOwnPaymentProviderFormFieldBlock({
                        fieldName: 'name',
                        fieldLabel: generalMessages.name,
                    })}
                    {getOwnPaymentProviderFormFieldBlock({
                        fieldName: 'address',
                        fieldLabel: generalMessages.address,
                    })}
                    {getOwnPaymentProviderFormFieldBlock({
                        fieldName: 'uic',
                        fieldLabel: parkingManagementMessages.uic,
                    })}
                    {getOwnPaymentProviderFormFieldBlock({
                        fieldName: 'tax_number',
                        fieldLabel: parkingManagementMessages.taxNumber,
                    })}
                    {getOwnPaymentProviderFormFieldBlock({
                        fieldName: 'email',
                        fieldLabel: commonMessages.email,
                    })}
                    <FormFieldBlock>
                        <FormFieldBlockLabel>
                            <FormFieldBlockTitle>
                                <FormFieldBlockName>
                                    {formatMessage(parkingManagementMessages.myPosKeyIndex)}
                                </FormFieldBlockName>
                            </FormFieldBlockTitle>
                        </FormFieldBlockLabel>
                        <FormFieldBlockBody className="w-full">
                            <FormField
                                control={form.control}
                                name="phone"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                type="tel"
                                                {...field}
                                                placeholder={formatMessage(reservationMessages.phoneNumber)}
                                                value={field.value ?? ''}
                                                onKeyDown={(e) => {
                                                    if (!/[0-9+\-()]/.test(e.key) && e.key.length === 1) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </FormFieldBlockBody>
                    </FormFieldBlock>
                </>
            )}

            <div className="flex justify-end">
                <Button className="w-full sm:w-auto" type="submit" disabled={!form.formState.isDirty}>
                    {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-lg" />}
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default PaymentProviders;
